import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ago, datetime, isStale, money, num } from "../lib/format";
import { useNow } from "../lib/useNow";
import { useDataStatus, useSyncProgress, triggerSync } from "../lib/queries";
import { useAuth } from "../lib/auth";
import { toast } from "../lib/toast";
import { api } from "../lib/api";
import type { DashboardData } from "../lib/types";
import { ProgressCard, type ProgressStat, type TaskState } from "./ProgressCard";
import { cn } from "./ui";
import { Icon } from "../lib/icons";

const STAGE_LABEL: Record<string, string> = {
  extract: "Загрузка данных из Google Sheets",
  transform: "Преобразование данных",
  stock_engine: "Пересчёт остатков",
  quality: "Проверка качества данных",
  views: "Обновление представлений",
};

/** Header chip: when the data was last refreshed. The pipeline runs on a fixed cron (every 2h);
 *  admins additionally get a manual "обновить сейчас" button here, with a real-progress popup —
 *  everyone else stays read-only. 2026-09-11: re-added (admin-only) after being fully removed
 *  earlier; a same-day follow-up added the progress modal per explicit request ("animatsiya
 *  qoshaylik popup oyna bilan ... haqiqiy yangilanishni" — real stages, not a fake spinner).
 */
export function DataStatus() {
  const { data: status, refetch } = useDataStatus();
  const { can } = useAuth();
  const [open, setOpen] = useState(false); // progress modal
  const [taskState, setTaskState] = useState<TaskState>("running");
  const [snapshot, setSnapshot] = useState<ProgressStat[] | undefined>();
  const polling = open && taskState === "running";
  const { data: progress } = useSyncProgress(polling);

  const healthy = status?.healthy ?? true;
  const running = status?.state === "RUNNING";
  const when = status?.last_success || status?.last_update;
  const now = useNow();
  const stale = !running && isStale(when, now);
  // Обзор shows the same warning (plus next-run time) in its header — don't say it twice
  const onOverview = useLocation().pathname === "/";

  // /sync/progress's own `running` flag is what OUR trigger actually set — poll-granularity 1s,
  // so it's correct even when the sync fails in under a second (too fast for the 60s-by-default
  // /meta/status poll to ever observe a RUNNING state at all — the bug behind "ancha vaqtdan beri
  // turibdi", modal stuck forever on "Запуск…", 2026-09-11).
  //
  // `outcome` is what the run itself reported (2026-10-05): before that, a sync skipped because the
  // cron already held the lock — or one that failed and rolled its etl_run row back — looked
  // exactly like success here ("Данные обновлены" for a run that changed nothing).
  useEffect(() => {
    if (!polling || progress === undefined || progress.running) return;
    refetch().then(({ data }) => {
      if (progress.outcome === "SKIPPED") {
        setOpen(false);
        toast("Сейчас идёт плановое обновление — данные обновятся через пару минут");
        return;
      }
      const failed = progress.outcome === "FAILED" || data?.state === "FAILED";
      setTaskState(failed ? "error" : "done");
      // Show what the warehouse looks like right now — the popup answers "so what changed", not
      // just "done" (2026-09-12, explicit request: "tekushiy sostoyaniya sklad oynasiga bersin").
      if (!failed) {
        api
          .get<DashboardData>("/dashboard", { trend_days: 7 })
          .then((r) => {
            const k = r.data.kpis;
            setSnapshot([
              { label: "Остаток", value: num(k.stock.value), icon: "box" },
              { label: "Себестоимость", value: money(k.stock_value.value), icon: "scale" },
              {
                label: "Проблемы",
                value: num(k.problems.critical + k.problems.warning),
                icon: "alert",
                danger: k.problems.critical > 0,
              },
            ]);
          })
          .catch(() => setSnapshot(undefined));
      }
    });
  }, [polling, progress, refetch]);

  const refresh = async () => {
    try {
      const r = await triggerSync();
      if (r.started) {
        setTaskState("running");
        setSnapshot(undefined);
        setOpen(true);
      } else {
        toast(r.reason === "already running" ? "Уже обновляется" : "Не удалось запустить");
      }
      refetch();
    } catch {
      toast("Не удалось запустить обновление");
    }
  };

  const stage = progress?.stage ? (STAGE_LABEL[progress.stage] ?? progress.stage) : null;

  return (
    <span className="inline-flex items-center gap-1">
      {/* Timestamp stays out of the header (2026-09-12 request) — it only appears here as a
          warning once the data is older than the 2-hour ETL cadence allows. */}
      {stale && !onOverview && (
        <span
          className="hidden items-center gap-1.5 rounded-lg bg-warn-weak px-2.5 py-1.5 text-xs font-medium text-warn sm:inline-flex"
          title={`Последнее обновление: ${datetime(when)}\nОбычно данные обновляются каждые 2 часа`}
        >
          <Icon name="alert" size={14} />
          Данные устарели · {ago(when, now)}
        </span>
      )}
      <span
        title={
          status
            ? [
                running ? "Сейчас идёт обновление данных…" : stale ? "Данные устарели" : "Данные в порядке",
                `Обновлено: ${datetime(when)} (${ago(when, now)})`,
                status.next_run && !running ? `Следующее обновление: ${datetime(status.next_run).slice(11, 16)}` : "",
                `Данные по: ${status.data_through ? datetime(status.data_through).slice(0, 10) : "—"}`,
                status.state && status.state !== "SUCCESS" && !running ? "Последний запуск завершился с ошибкой" : "",
              ]
                .filter(Boolean)
                .join("\n")
            : "Статус данных"
        }
        className="grid h-9 w-9 flex-none place-items-center rounded-lg text-text-2"
      >
        {/* Timestamp text dropped from the header (2026-09-12, "yuqoridagi yangilangan vaqtini
            olib tashlaymiz headbar dagi") — the Обзор hero now owns that message; this dot +
            tooltip stays as a compact at-a-glance health check. */}
        <span
          className={cn(
            "h-2 w-2 flex-none rounded-full",
            running ? "animate-pulse bg-accent" : healthy && !stale ? "bg-ok" : "bg-warn",
          )}
        />
      </span>
      {can("admin") && (
        <button
          type="button"
          onClick={refresh}
          disabled={running}
          title={running ? "Данные уже обновляются…" : "Обновить данные из Google Sheets сейчас"}
          className="grid h-9 w-9 flex-none place-items-center rounded-lg text-text-2 hover:bg-surface-2 hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Icon name="refresh" size={16} className={cn(running && "animate-spin")} />
        </button>
      )}

      {open && (
        <ProgressCard
          title={
            taskState === "running"
              ? "Обновление данных"
              : taskState === "done"
                ? "Данные обновлены"
                : "Ошибка обновления"
          }
          state={taskState}
          pct={taskState === "running" ? (progress?.pct ?? undefined) : 100}
          stage={taskState === "running" ? (stage ?? "Запуск…") : undefined}
          stats={taskState === "done" ? snapshot : undefined}
          detail={
            taskState === "done"
              ? `Обновлено: ${datetime(status?.last_success ?? undefined)}`
              : taskState === "error"
                ? "Смотрите лог синхронизации на сервере."
                : undefined
          }
          closable={taskState !== "running"}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}
